import React, { ReactNode, ReactElement } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { TableBody } from '@mui/material';
import { DatagridBodyProps, MutationMode } from 'react-admin';

import EditableDatagridRow from './EditableDatagridRow';
import EditableDatagridCreateForm from './EditableDatagridCreateForm';
import { DatagridClasses } from './EditableDatagrid';

const EditableDatagridBody = (props: EditableDatagridBodyProps) => {
    const {
        children,
        className,
        data,
        expand,
        hasBulkActions,
        hover,
        onToggleItem,
        resource,
        rowClick,
        rowStyle,
        selectedIds,
        isRowSelectable,
        editForm,
        createForm,
        hasStandaloneCreateForm = false,
        isStandaloneCreateFormVisible,
        closeStandaloneCreateForm,
        mutationMode,
        ...rest
    } = props;

    return (
        <TableBody
            className={clsx('datagrid-body', className, DatagridClasses.tbody)}
            {...rest}
        >
            {createForm && (
                <EditableDatagridCreateForm
                    closeStandaloneCreateForm={closeStandaloneCreateForm}
                    createForm={createForm}
                    expand={expand as any}
                    hasBulkActions={hasBulkActions}
                    hasStandaloneCreateForm={hasStandaloneCreateForm}
                    isStandaloneCreateFormVisible={
                        isStandaloneCreateFormVisible
                    }
                    resource={resource}
                />
            )}
            {data.map((record, rowIndex) => (
                <EditableDatagridRow
                    className={clsx(DatagridClasses.row, {
                        [DatagridClasses.rowEven]: rowIndex % 2 === 0,
                        [DatagridClasses.rowOdd]: rowIndex % 2 !== 0,
                        [DatagridClasses.clickableRow]: rowClick,
                    })}
                    expand={expand}
                    form={editForm}
                    hasBulkActions={hasBulkActions}
                    hover={hover}
                    id={record.id}
                    key={record.id}
                    onToggleItem={onToggleItem}
                    record={record}
                    resource={resource}
                    rowClick={rowClick}
                    selectable={!isRowSelectable || isRowSelectable(record)}
                    selected={selectedIds.includes(record.id)}
                    style={rowStyle ? rowStyle(record, rowIndex) : null}
                    mutationMode={mutationMode}
                >
                    {children}
                </EditableDatagridRow>
            ))}
        </TableBody>
    );
};

export interface EditableDatagridBodyProps extends DatagridBodyProps {
    children?: ReactNode;
    editForm?: ReactElement;
    createForm?: ReactElement;
    mutationMode?: MutationMode;
    hasStandaloneCreateForm?: boolean;
    isStandaloneCreateFormVisible: boolean;
    closeStandaloneCreateForm: () => void;
}

EditableDatagridBody.propTypes = {
    className: PropTypes.string,
    children: PropTypes.node,
    data: PropTypes.arrayOf(PropTypes.any).isRequired,
    expand: PropTypes.oneOfType([PropTypes.element, PropTypes.elementType]),
    hasBulkActions: PropTypes.bool.isRequired,
    hover: PropTypes.bool,
    onToggleItem: PropTypes.func,
    resource: PropTypes.string,
    rowClick: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    rowStyle: PropTypes.func,
    selectedIds: PropTypes.arrayOf(PropTypes.any),
    isRowSelectable: PropTypes.func,
    version: PropTypes.number,
};

EditableDatagridBody.defaultProps = {
    data: [],
    hasBulkActions: false,
};

// trick material-ui Table into thinking this is one of the child type it supports
// @ts-ignore
EditableDatagridBody.muiName = 'TableBody';

export default EditableDatagridBody;
